import { Facebook, Instagram } from "lucide-react";

/**
 * XIcon — the official X (formerly Twitter) mark as an inline SVG.
 *
 * Lucide 0.516 does not yet ship an X icon (still ships the old Twitter bird),
 * so we render the official brand glyph directly. Path is the standard X
 * corporate mark rendered at 24×24; caller controls size via the `size` prop
 * so it composes cleanly with the Facebook / Instagram Lucide icons.
 *
 * `strokeWidth` is accepted for API symmetry with Lucide icons but ignored:
 * the X mark is a solid filled glyph, not a stroked one.
 */
const XIcon = ({ size = 16, className = "", strokeWidth: _sw, ...rest }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
    {...rest}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

/**
 * SocialLinks — reusable Facebook + Instagram + X row.
 *
 * Used in the public landing footer AND the authenticated sidebar. Icons only,
 * no URL text ever visible. Opens in a new tab with a hardened rel attribute.
 *
 * Props:
 *   size       icon pixel size (default 16 — matches the sidebar nav icons)
 *   className  extra classes on the container (spacing/orientation)
 *   iconClass  extra classes on each anchor (padding, border, etc.)
 *   tone       "muted" (default) uses text-muted-foreground; "sidebar" uses
 *              the same tone as sidebar nav items so it visually belongs.
 *   testId     data-testid on the container (default "social-links")
 */
const LINKS = [
  {
    key: "facebook",
    label: "Visit CareerOS on Facebook",
    href: "https://www.facebook.com/profile.php?id=61592082026263",
    Icon: Facebook,
  },
  {
    key: "instagram",
    label: "Visit CareerOS on Instagram",
    href: "https://www.instagram.com/ai.careeros/",
    Icon: Instagram,
  },
  {
    key: "x",
    label: "Follow CareerOS on X",
    href: "https://x.com/CareerOSAi",
    Icon: XIcon,
  },
];

export function SocialLinks({
  size = 16,
  className = "",
  iconClass = "",
  testId = "social-links",
}) {
  return (
    <div
      className={`flex items-center gap-2 ${className}`}
      data-testid={testId}
      role="group"
      aria-label="CareerOS social media"
    >
      {LINKS.map(({ key, label, href, Icon }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          data-testid={`social-link-${key}`}
          className={`
            group inline-flex items-center justify-center rounded-md p-1.5
            text-muted-foreground
            transition-all duration-200 ease-out
            hover:text-primary hover:scale-110 hover:bg-primary/10
            hover:shadow-[0_0_16px_-2px_hsl(var(--primary)/0.55)]
            focus:outline-none focus-visible:text-primary
            focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background
            cursor-pointer
            ${iconClass}
          `}
        >
          <Icon size={size} strokeWidth={1.75} aria-hidden />
        </a>
      ))}
    </div>
  );
}

export default SocialLinks;
